import { useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FileIcon } from '@trinity/chat-components/components/icons/File';
import { cx } from '@trinity/chat-components/utils';
import styles from './style/FileDownloadActivity.module.css';

type Props = {
  error?: string;
  extension?: string;
  filename?: string;
  index: number;
  onCancel?: () => void;
  onRemove?: () => void;
  status: 'init' | 'transferring' | 'error' | 'complete';
  title?: string;
};

export const FileDownloadActivityItem = ({ onRemove, ...props }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;

    if (node !== null) {
      node.addEventListener('animationend', onRemove);
    }

    return () => {
      node?.removeEventListener('animationend', onRemove);
    };
  }, [onRemove]);

  const error = props.status === 'error';
  const complete = props.status === 'complete';
  const showCloseButton = error || complete;

  let titleText: string | undefined;

  if (error) {
    // Keeps whatever the caller was doing in view; not every caller supplies a title.
    titleText = props.title ? `${props.title} - Failed` : `Something went wrong`;
  } else if (complete) {
    titleText = `Upload complete`;
  } else {
    titleText = props.title;
  }

  return (
    <div
      className={cx(styles.root, {
        [styles.transferring]: props.status === 'transferring',
      })}
      ref={ref}
      style={{ bottom: props.index * 110 + (props.index + 1) * 10 }}
    >
      <div className={styles.wrap}>
        <div className={cx(styles.main, { [styles.settled]: error || complete })}>
          {showCloseButton && (
            <button className={styles.close} onClick={onRemove}>
              <CloseIcon />
            </button>
          )}
          {titleText && <div className={styles.title}>{titleText}</div>}
          <div className={styles.content}>
            <div className={styles.icon}>
              {error ? (
                <ErrorOutlineIcon className={styles.errorIcon} />
              ) : (
                <FileIcon extension={props.extension} size={30} />
              )}
            </div>
            <div className={styles.info}>
              <div className={styles.name} title={props.filename}>
                {props.filename ?? 'Untitled'}
              </div>
              {error && props.error && (
                <div className={styles.reason} title={props.error}>
                  {props.error}
                </div>
              )}
              {!error && !complete && (
                <div className={styles.stats}>
                  <div className={styles.bar}>
                    <span className={styles.one} />
                    <span className={styles.two} />
                  </div>
                  <button className={styles.cancel} onClick={props.onCancel}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FileDownloadActivityItem.displayName = 'FileDownloadActivity.Item';
